import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { FaCamera, FaCheck, FaRedo, FaSearchMinus, FaSearchPlus, FaTimes, FaUndo } from 'react-icons/fa';
import { FieldShell } from './FieldShell';
import { FieldSelect } from './FieldSelect';
import { useDomFieldId } from './_useFieldName';
import { onCropImage, CROP_ASPECTS } from './_cropImage';

const BLOCK = 'LStudio__app_field';

/*
    🔥 IMAGEN CON RECORTE — react-easy-crop

    Flujo en tres pasos: elegís archivo → recortás → confirmás.
    Recién al confirmar se genera el archivo recortado y se guarda.

    ⚠️ react-easy-crop DEVUELVE COORDENADAS, NO IMAGEN
       El recorte real lo hace _cropImage.js con un canvas. Así el
       backend recibe la imagen lista y liviana, y no tiene que
       recortar de nuevo en cada lugar donde se muestre.

    npm i react-easy-crop
*/

export const FieldImageCrop = ({
    id, label = 'Imagen', hint, error,
    value = null,                     // File | string (url) | null
    onChange,
    aspect = 1, aspectOptions = null,
    cropShape = 'rect',               // rect | round
    maxWidth = 1200, quality = 0.9, maxSize = 5242880,
    isDisabled = false, span = 2,
}) => {

    const REF__useRefInput = useRef(null);

    const [useSource, setSource] = useState(null);      // dataURL a recortar
    const [useCrop, setCrop] = useState({ x: 0, y: 0 });
    const [useZoom, setZoom] = useState(1);
    const [useRotation, setRotation] = useState(0);
    const [useAspect, setAspect] = useState(aspect);
    const [useArea, setArea] = useState(null);
    const [useIsWorking, setIsWorking] = useState(false);
    const [useLocalError, setLocalError] = useState(null);

    const PREVIEW = useMemo(() => {
        if (!value) return null;
        return typeof value === 'string' ? value : URL.createObjectURL(value);
    }, [value]);

    useEffect(() => () => {
        if (PREVIEW && typeof value !== 'string') URL.revokeObjectURL(PREVIEW);
    }, [PREVIEW, value]);

    const onPickFile = (event) => {

        const FILE = event.target.files?.[0];

        if (!FILE) return;

        if (FILE.size > maxSize) {
            setLocalError(`La imagen supera los ${Math.round(maxSize / 1048576)} MB`);
            return;
        }

        setLocalError(null);

        const READER = new FileReader();
        READER.addEventListener('load', () => {
            setSource(READER.result);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setRotation(0);
        });
        READER.readAsDataURL(FILE);
    };

    const CB__onCropComplete = useCallback((_area, areaPixels) => setArea(areaPixels), []);

    const onConfirm = async () => {

        if (!useArea) return;

        setIsWorking(true);

        try {
            const FILE = await onCropImage(useSource, useArea, {
                rotation: useRotation, maxWidth, quality,
            });
            onChange?.(FILE);
            onCancel();
        } catch (err) {
            setLocalError('No se pudo procesar la imagen');
        } finally {
            setIsWorking(false);
        }
    };

    const onCancel = () => {
        setSource(null);
        setArea(null);
        if (REF__useRefInput.current) REF__useRefInput.current.value = '';
    };

    const onRemove = () => {
        onChange?.(null);
        onCancel();
    };

    const DOM_ID = useDomFieldId(id);

    return (
        <FieldShell id={DOM_ID} label={label} span={span} isDisabled={isDisabled}
            error={error ?? useLocalError}
            hint={hint ?? `Hasta ${Math.round(maxSize / 1048576)} MB · se recorta antes de subir`} >

            <input ref={REF__useRefInput} id={DOM_ID} type="file" accept="image/*"
                className={`${BLOCK}__avatar_input`} onChange={onPickFile} disabled={isDisabled} />

            {
                useSource
                    ? (
                        /* ---------------- 🔥 MODO RECORTE ---------------- */
                        <div className={`${BLOCK}__crop`}>

                            <div className={`${BLOCK}__crop_stage`}>
                                <Cropper
                                    image={useSource}
                                    crop={useCrop} zoom={useZoom} rotation={useRotation}
                                    aspect={useAspect} cropShape={cropShape}
                                    onCropChange={setCrop} onZoomChange={setZoom}
                                    onRotationChange={setRotation}
                                    onCropComplete={CB__onCropComplete}
                                    showGrid={cropShape === 'rect'}
                                />
                            </div>

                            <div className={`${BLOCK}__crop_tools`}>

                                <div className={`${BLOCK}__crop_group`}>
                                    <button type="button" aria-label="Alejar"
                                        onClick={() => setZoom((z) => Math.max(1, z - 0.2))} >
                                        <FaSearchMinus />
                                    </button>
                                    <input type="range" className={`${BLOCK}__range_input`}
                                        min={1} max={4} step={0.05} value={useZoom}
                                        style={{ '--LS__range_percent': `${((useZoom - 1) / 3) * 100}%` }}
                                        onChange={(event) => setZoom(Number(event.target.value))}
                                        aria-label="Zoom" />
                                    <button type="button" aria-label="Acercar"
                                        onClick={() => setZoom((z) => Math.min(4, z + 0.2))} >
                                        <FaSearchPlus />
                                    </button>
                                </div>

                                <div className={`${BLOCK}__crop_group`}>
                                    <button type="button" aria-label="Rotar a la izquierda"
                                        onClick={() => setRotation((r) => r - 90)} >
                                        <FaUndo />
                                    </button>
                                    <button type="button" aria-label="Rotar a la derecha"
                                        onClick={() => setRotation((r) => r + 90)} >
                                        <FaRedo />
                                    </button>
                                </div>

                                {
                                    (aspectOptions ?? CROP_ASPECTS).length > 1 && aspectOptions !== false && (
                                        <div className={`${BLOCK}__crop_aspect`}>
                                            <FieldSelect isBare value={useAspect}
                                                options={aspectOptions ?? CROP_ASPECTS}
                                                onChange={(event) => setAspect(Number(event.target.value))}
                                                isSearchable={false} />
                                        </div>
                                    )
                                }

                            </div>

                            <div className={`${BLOCK}__crop_actions`}>

                                <button type="button" className={`${BLOCK}__avatar_button`}
                                    onClick={onCancel} disabled={useIsWorking} >
                                    <FaTimes /> Cancelar
                                </button>

                                <button type="button"
                                    className={`${BLOCK}__avatar_button ${BLOCK}__crop_confirm`}
                                    onClick={onConfirm} disabled={useIsWorking || !useArea} >
                                    <FaCheck /> {useIsWorking ? 'Procesando…' : 'Recortar y usar'}
                                </button>

                            </div>

                        </div>
                    )
                    : (
                        /* ---------------- 🔥 MODO VISTA ---------------- */
                        <div className={`${BLOCK}__avatar`}>

                            <div className={`${BLOCK}__avatar_preview`}
                                data-shape={cropShape}
                                style={{ width: 110, height: cropShape === 'round' ? 110 : 110 / useAspect }} >
                                {
                                    PREVIEW
                                        ? <img src={PREVIEW} alt="" className={`${BLOCK}__avatar_img`} />
                                        : <FaCamera className={`${BLOCK}__avatar_empty`} />
                                }
                            </div>

                            <div className={`${BLOCK}__avatar_actions`}>

                                <button type="button" className={`${BLOCK}__avatar_button`}
                                    disabled={isDisabled} onClick={() => REF__useRefInput.current?.click()} >
                                    <FaCamera /> {PREVIEW ? 'Cambiar' : 'Elegir imagen'}
                                </button>

                                {
                                    PREVIEW && (
                                        <button type="button"
                                            className={`${BLOCK}__avatar_button ${BLOCK}__avatar_button--danger`}
                                            disabled={isDisabled} onClick={onRemove} >
                                            <FaTimes /> Quitar
                                        </button>
                                    )
                                }

                            </div>

                        </div>
                    )
            }

        </FieldShell>
    );
};
